<script lang="ts">
  import { 
    X, Printer, Download, Droplets, Flame, Building2, 
    CheckCircle2, AlertTriangle, ShieldCheck, Receipt, User, Calendar, MapPin,
    CreditCard, ExternalLink, ArrowRight, Send, FileSpreadsheet
  } from 'lucide-svelte';
  import { unitPreference, type UnitPreference } from '../stores/preferences';

  interface InvoiceUnit {
    unit_id: string;
    unit_label: string;
    floor_label: string;
    tenant_name: string;
    meter_count?: number;
    cold_water_gallons: number;
    hot_water_gallons: number;
    submetered_gallons: number;
    common_area_gallons: number;
    total_billed_gallons: number;
    submetered_cost_usd: number;
    common_area_cost_usd: number;
    total_billed_cost_usd: number;
    baseline_gallons?: number;
    variance_percent?: number;
    status?: string;
    pay_token?: string;
    payment_status?: string;
    due_date?: string;
  }

  interface BillingSummary {
    property_id: number;
    property_name: string;
    property_address?: string;
    billing_period: string;
    start_date: string;
    end_date: string;
    water_rate_per_thousand_gal: number;
    hot_water_surcharge_percent: number;
    available_units?: InvoiceUnit[];
  }

  let { 
    open = $bindable(false),
    property,
    summary,
    unit = $bindable<InvoiceUnit | null>(null),
    availableUnits = []
  } = $props<{
    open: boolean;
    property?: any;
    summary?: BillingSummary | null;
    unit: InvoiceUnit | null;
    availableUnits?: InvoiceUnit[];
  }>();

  let dispatchingNotice = $state(false);
  let dispatchNoticeFeedback = $state('');

  function formatGallons(gallons: number | null | undefined, pref: UnitPreference): string {
    if (gallons == null || isNaN(gallons)) return '0 gal';
    if (pref === 'metric') {
      const liters = gallons * 3.78541;
      return `${Math.round(liters).toLocaleString()} L`;
    }
    return `${Math.round(gallons).toLocaleString()} gal`;
  }

  function formatCurrency(val: number | null | undefined): string {
    if (val == null || isNaN(val)) return '$0.00';
    return `$${val.toFixed(2)}`;
  }

  const propName = $derived(
    summary?.property_name || property?.property_name || `Property #${summary?.property_id || property?.property_id || '700'}`
  );

  const propAddress = $derived(
    summary?.property_address || 
    (property?.address ? `${property.address}, ${property.city || ''} ${property.state || ''}` : '100 South Grand Avenue, Los Angeles, CA 90012')
  );

  const billingPeriod = $derived(summary?.billing_period || '2026-09');
  const startDate = $derived(summary?.start_date || '2026-09-01');
  const endDate = $derived(summary?.end_date || '2026-09-30');
  const ratePerKGal = $derived(summary?.water_rate_per_thousand_gal || 12.50);
  const hwSurcharge = $derived(summary?.hot_water_surcharge_percent || 15.0);
  const effectiveHwRate = $derived(ratePerKGal * (1 + hwSurcharge / 100));

  const invoiceNumber = $derived(
    `INV-${billingPeriod.replace('-', '')}-${unit?.unit_label ? unit.unit_label.replace(/[^a-zA-Z0-9]/g, '') : '101'}`
  );

  function printInvoice() {
    window.print();
  }

  function exportCSV() {
    if (!unit) return;
    const headers = [
      'Invoice Number', 'Property', 'Address', 'Billing Period', 'Unit', 'Tenant Name',
      'Cold Water (Gal)', 'Hot Water (Gal)', 'Submetered Total (Gal)', 'Common Area Share (Gal)',
      'Total Billed (Gal)', 'Submetered Cost ($)', 'Common Area Cost ($)', 'Total Amount Due ($)', 'Due Date'
    ];
    const row = [
      invoiceNumber,
      `"${propName.replace(/"/g, '""')}"`,
      `"${propAddress.replace(/"/g, '""')}"`,
      billingPeriod,
      `"${unit.unit_label}"`,
      `"${unit.tenant_name.replace(/"/g, '""')}"`,
      unit.cold_water_gallons.toFixed(1),
      unit.hot_water_gallons.toFixed(1),
      unit.submetered_gallons.toFixed(1),
      unit.common_area_gallons.toFixed(1),
      unit.total_billed_gallons.toFixed(1),
      unit.submetered_cost_usd.toFixed(2),
      unit.common_area_cost_usd.toFixed(2),
      unit.total_billed_cost_usd.toFixed(2),
      '2026-10-15'
    ];
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), row.join(',')].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${invoiceNumber}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function exportJSON() {
    if (!unit) return;
    const payload = {
      invoice_number: invoiceNumber,
      property: {
        id: summary?.property_id || property?.property_id,
        name: propName,
        address: propAddress
      },
      billing_period: billingPeriod,
      statement_dates: {
        start: startDate,
        end: endDate,
        due: '2026-10-15'
      },
      resident: {
        unit_id: unit.unit_id,
        unit_label: unit.unit_label,
        floor_label: unit.floor_label,
        name: unit.tenant_name
      },
      submeter_consumption: {
        cold_water_gallons: unit.cold_water_gallons,
        hot_water_gallons: unit.hot_water_gallons,
        submetered_gallons: unit.submetered_gallons,
        common_area_gallons: unit.common_area_gallons,
        total_billed_gallons: unit.total_billed_gallons
      },
      financials: {
        rate_per_kgal: ratePerKGal,
        hot_water_surcharge_percent: hwSurcharge,
        submetered_cost_usd: unit.submetered_cost_usd,
        common_area_cost_usd: unit.common_area_cost_usd,
        total_billed_cost_usd: unit.total_billed_cost_usd,
        currency: 'USD'
      },
      payment: {
        pay_token: unit.pay_token || `pay_${unit.unit_id.slice(0, 8)}`,
        status: unit.payment_status || 'unpaid'
      },
      exported_at: new Date().toISOString()
    };
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(payload, null, 2));
    const link = document.createElement('a');
    link.setAttribute('href', dataStr);
    link.setAttribute('download', `${invoiceNumber}.json`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async function dispatchNotice() {
    if (!unit) return;
    dispatchingNotice = true;
    dispatchNoticeFeedback = '';
    try {
      await new Promise(r => setTimeout(r, 400));
      dispatchNoticeFeedback = `Statement dispatched to ${unit.tenant_name} via Email & SMS link!`;
      setTimeout(() => {
        dispatchNoticeFeedback = '';
      }, 4000);
    } catch (e: any) {
      dispatchNoticeFeedback = `Failed to dispatch: ${e.message}`;
    } finally {
      dispatchingNotice = false;
    }
  }

  function selectUnit(e: Event) {
    const target = e.target as HTMLSelectElement;
    const found = availableUnits.find(u => u.unit_id === target.value);
    if (found) {
      unit = found;
    }
  }
</script>

{#if open && unit}
  <div class="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 print:p-0 print:bg-white print:static">
    <div class="bg-white rounded-3xl shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden border border-slate-200 print:shadow-none print:border-none print:max-w-none print:rounded-none">
      
      <!-- Top Action Bar (hidden in print) -->
      <div class="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/80 print:hidden">
        <div class="flex items-center gap-3">
          <div class="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
            <Receipt class="w-5 h-5" />
          </div>
          <div>
            <h3 class="font-bold text-slate-800 text-sm leading-tight">Utility Submeter Invoice</h3>
            <p class="text-xs text-slate-500">Official itemized water billing invoice</p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <!-- Unit Switcher if multiple units provided -->
          {#if availableUnits && availableUnits.length > 1}
            <div class="flex items-center gap-1.5 text-xs text-slate-600">
              <span class="font-semibold">Unit:</span>
              <select 
                class="select select-xs select-bordered bg-white text-xs font-semibold rounded-lg"
                value={unit.unit_id}
                onchange={selectUnit}
              >
                {#each availableUnits as u}
                  <option value={u.unit_id}>Unit {u.unit_label} ({u.tenant_name})</option>
                {/each}
              </select>
            </div>
          {/if}

          <!-- Pay Online Button -->
          {#if unit.pay_token}
            <a 
              href="#/pay/{unit.pay_token}" 
              target="_blank"
              class="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all shadow-sm active:scale-95"
            >
              <CreditCard class="w-3.5 h-3.5" />
              <span>Pay Online</span>
              <ExternalLink class="w-3 h-3 opacity-70" />
            </a>
          {/if}

          <!-- Export CSV Button -->
          <button 
            type="button"
            class="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all active:scale-95 border border-slate-200"
            onclick={exportCSV}
            title="Download CSV Statement"
          >
            <FileSpreadsheet class="w-3.5 h-3.5 text-emerald-600" />
            <span class="hidden sm:inline">Export CSV</span>
          </button>

          <!-- Export JSON Button -->
          <button 
            type="button"
            class="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all active:scale-95 border border-slate-200"
            onclick={exportJSON}
            title="Download JSON Statement"
          >
            <Download class="w-3.5 h-3.5 text-primary" />
            <span class="hidden sm:inline">JSON</span>
          </button>

          <!-- Send Notice Button -->
          <button 
            type="button"
            class="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all active:scale-95 border border-slate-200 disabled:opacity-50"
            onclick={dispatchNotice}
            disabled={dispatchingNotice}
            title="Dispatch Statement Notice via Email & SMS"
          >
            <Send class="w-3.5 h-3.5 text-cyan-600" />
            <span class="hidden sm:inline">{dispatchingNotice ? 'Sending...' : 'Send Notice'}</span>
          </button>

          <!-- Print / PDF Button -->
          <button 
            class="px-3 py-1.5 bg-primary text-white hover:bg-primary-focus rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all shadow-sm active:scale-95"
            onclick={printInvoice}
          >
            <Printer class="w-3.5 h-3.5" />
            Print / PDF
          </button>

          <!-- Close Modal -->
          <button 
            class="w-8 h-8 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 flex items-center justify-center transition-colors"
            onclick={() => open = false}
          >
            <X class="w-4 h-4" />
          </button>
        </div>
      </div>

      <!-- Dispatch Notice Feedback Banner -->
      {#if dispatchNoticeFeedback}
        <div class="px-6 py-2.5 bg-emerald-50 border-b border-emerald-200 text-emerald-800 text-xs font-bold flex items-center justify-between animate-fade-in print:hidden">
          <div class="flex items-center gap-2">
            <CheckCircle2 class="w-4 h-4 text-emerald-600" />
            <span>{dispatchNoticeFeedback}</span>
          </div>
          <button type="button" class="text-emerald-700 hover:text-emerald-900 text-xs" onclick={() => dispatchNoticeFeedback = ''}>✕</button>
        </div>
      {/if}

      <!-- Printable Invoice Sheet -->
      <div class="p-6 sm:p-8 space-y-6 text-slate-800 font-sans print:p-6 print:space-y-4 print:text-black">
        
        <!-- Header: Property & Statement Metadata -->
        <div class="flex flex-col sm:flex-row justify-between items-start gap-4 pb-6 border-b-2 border-slate-800">
          <div>
            <div class="flex items-center gap-2 text-primary font-black tracking-wider text-base uppercase mb-1">
              <Droplets class="w-5 h-5 fill-primary text-primary" />
              <span>Saya Smart Submetering</span>
            </div>
            <h1 class="text-xl sm:text-2xl font-black text-slate-900 leading-tight">{propName}</h1>
            <p class="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
              <MapPin class="w-3.5 h-3.5 text-slate-400" />
              {propAddress}
            </p>
          </div>

          <div class="sm:text-right bg-slate-50 print:bg-transparent p-3 sm:p-0 rounded-xl w-full sm:w-auto">
            <span class="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-slate-900 text-white mb-1.5">
              Utility Statement
            </span>
            <div class="font-mono text-sm font-black text-slate-900">{invoiceNumber}</div>
            <div class="text-xs text-slate-500 font-medium mt-0.5">Billing Period: <span class="font-bold text-slate-700">{billingPeriod}</span></div>
            <div class="text-xs text-slate-500 font-medium">Due Date: <span class="font-bold text-rose-600">October 15, 2026</span></div>
          </div>
        </div>

        <!-- Resident & Meter Info Bar -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100 print:bg-slate-50 print:border-slate-300">
          <div>
            <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Billed Resident / Tenant</span>
            <div class="font-black text-slate-900 text-base flex items-center gap-1.5">
              <User class="w-4 h-4 text-slate-400" />
              {unit.tenant_name || 'Resident'}
            </div>
            <div class="text-xs text-slate-600 font-semibold mt-0.5">
              Suite {unit.unit_label} • Floor {unit.floor_label || '1'}
            </div>
          </div>

          <div class="sm:text-right">
            <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Metering Technology</span>
            <div class="text-xs font-semibold text-slate-800 flex sm:justify-end items-center gap-1.5">
              <ShieldCheck class="w-4 h-4 text-emerald-600" />
              LoRaWAN Ultrasonic AMR Submeters ({unit.meter_count ?? 2} Devices)
            </div>
            <div class="text-[11px] text-slate-500 mt-0.5">
              Service Dates: {startDate} through {endDate}
            </div>
          </div>
        </div>

        <!-- Itemized Utility Consumption Table -->
        <div class="overflow-hidden rounded-xl border border-slate-200">
          <table class="w-full text-left text-xs border-collapse">
            <thead>
              <tr class="bg-slate-100 text-slate-700 border-b border-slate-200 uppercase font-bold text-[10px] tracking-wider">
                <th class="py-2.5 px-4">Service Description</th>
                <th class="py-2.5 px-3 text-right">Consumed Volume</th>
                <th class="py-2.5 px-3 text-right">Utility Rate</th>
                <th class="py-2.5 px-4 text-right">Total Charge</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 text-slate-700">
              <!-- Cold Water -->
              <tr>
                <td class="py-3 px-4">
                  <div class="font-bold text-slate-900 flex items-center gap-1.5">
                    <Droplets class="w-3.5 h-3.5 text-sky-500" />
                    Cold Water Submeter
                  </div>
                  <div class="text-[11px] text-slate-400">In-suite cold water fixtures & appliances</div>
                </td>
                <td class="py-3 px-3 text-right font-mono font-bold text-slate-800">
                  {formatGallons(unit.cold_water_gallons, $unitPreference)}
                </td>
                <td class="py-3 px-3 text-right font-mono text-slate-600">
                  ${ratePerKGal.toFixed(2)} / kGal
                </td>
                <td class="py-3 px-4 text-right font-mono font-bold text-slate-900">
                  {formatCurrency((unit.cold_water_gallons / 1000) * ratePerKGal)}
                </td>
              </tr>

              <!-- Hot Water -->
              <tr>
                <td class="py-3 px-4">
                  <div class="font-bold text-slate-900 flex items-center gap-1.5">
                    <Flame class="w-3.5 h-3.5 text-amber-500" />
                    Domestic Hot Water Submeter
                  </div>
                  <div class="text-[11px] text-slate-400">Includes central hydronic heating fuel surcharge (+{hwSurcharge}%)</div>
                </td>
                <td class="py-3 px-3 text-right font-mono font-bold text-slate-800">
                  {formatGallons(unit.hot_water_gallons, $unitPreference)}
                </td>
                <td class="py-3 px-3 text-right font-mono text-slate-600">
                  ${effectiveHwRate.toFixed(2)} / kGal
                </td>
                <td class="py-3 px-4 text-right font-mono font-bold text-slate-900">
                  {formatCurrency((unit.hot_water_gallons / 1000) * effectiveHwRate)}
                </td>
              </tr>

              <!-- Common Area Allocation (RUBS) -->
              <tr class="bg-slate-50/60">
                <td class="py-3 px-4">
                  <div class="font-bold text-slate-900 flex items-center gap-1.5">
                    <Building2 class="w-3.5 h-3.5 text-slate-500" />
                    Common Area Proration (RUBS)
                  </div>
                  <div class="text-[11px] text-slate-400">Equal building share for landscape irrigation, fitness gym & lobby makeup</div>
                </td>
                <td class="py-3 px-3 text-right font-mono font-medium text-slate-600">
                  {formatGallons(unit.common_area_gallons, $unitPreference)}
                </td>
                <td class="py-3 px-3 text-right font-mono text-slate-500">
                  Prorata / Unit
                </td>
                <td class="py-3 px-4 text-right font-mono font-bold text-slate-900">
                  {formatCurrency(unit.common_area_cost_usd)}
                </td>
              </tr>
            </tbody>
            
            <!-- Summary Totals -->
            <tfoot>
              <tr class="border-t-2 border-slate-300 bg-slate-50 font-bold text-slate-800">
                <td colspan="3" class="py-2.5 px-4 text-right uppercase tracking-wider text-[11px]">Submetered & Shared Total:</td>
                <td class="py-2.5 px-4 text-right font-mono text-sm font-extrabold text-slate-900">
                  {formatCurrency(unit.total_billed_cost_usd)}
                </td>
              </tr>
              <tr class="bg-slate-900 text-white font-extrabold print:bg-slate-100 print:text-black print:border-t-2 print:border-black">
                <td colspan="3" class="py-3 px-4 text-right uppercase tracking-wider text-xs">Total Current Charges Due:</td>
                <td class="py-3 px-4 text-right font-mono text-base font-black text-white print:text-black">
                  {formatCurrency(unit.total_billed_cost_usd)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Conservation & Efficiency Scorecard -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-slate-50 p-3.5 rounded-2xl border border-slate-100 text-xs">
          <div>
            <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Historical Baseline</span>
            <span class="font-mono font-bold text-slate-800">{formatGallons(unit.baseline_gallons ?? (unit.submetered_gallons * 0.95), $unitPreference)}</span>
          </div>
          <div>
            <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Conservation Variance</span>
            <span class="font-bold {(unit.variance_percent ?? 0) > 15 ? 'text-rose-600' : (unit.variance_percent ?? 0) < -15 ? 'text-emerald-600' : 'text-slate-700'}">
              {(unit.variance_percent ?? 0) > 0 ? '+' : ''}{(unit.variance_percent ?? 0).toFixed(1)}% vs Baseline
            </span>
          </div>
          <div>
            <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">In-Suite Leak Audit</span>
            <span class="text-emerald-600 font-bold flex items-center gap-1">
              <CheckCircle2 class="w-3.5 h-3.5" /> 0 Alarms (Pass)
            </span>
          </div>
        </div>

        <!-- Online Bill Pay Callout & Dispatch Banner (Print: hidden) -->
        {#if unit.payment_status === 'paid'}
          <div class="p-4 bg-emerald-50 rounded-2xl border border-emerald-200 flex items-center justify-between gap-3 text-emerald-800 print:hidden">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center font-bold shadow-sm">
                <CheckCircle2 class="w-6 h-6" />
              </div>
              <div>
                <div class="text-xs font-black uppercase tracking-wider text-emerald-900">Invoice Paid in Full</div>
                <div class="text-[11px] text-emerald-700">Thank you! Your payment has been processed and credited to your resident account.</div>
              </div>
            </div>
            {#if unit.pay_token}
              <a 
                href="#/pay/{unit.pay_token}" 
                target="_blank"
                class="btn btn-xs btn-outline border-emerald-600 text-emerald-800 hover:bg-emerald-600 hover:text-white font-bold"
              >
                View Receipt
              </a>
            {/if}
          </div>
        {:else}
          <div class="p-4 bg-gradient-to-r from-primary/10 via-primary/5 to-emerald-500/5 rounded-2xl border border-primary/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 print:hidden">
            <div class="flex items-start gap-3">
              <div class="w-10 h-10 rounded-xl bg-primary text-white flex items-center justify-center font-bold shadow-sm shrink-0 mt-0.5">
                <CreditCard class="w-5 h-5" />
              </div>
              <div>
                <div class="text-xs font-black uppercase tracking-wider text-slate-900 flex items-center gap-2">
                  <span>Pay Current Charges Online</span>
                  <span class="badge badge-xs badge-primary font-bold uppercase">Instant</span>
                </div>
                <div class="text-[11px] text-slate-600 mt-0.5">
                  Direct mobile checkout with Apple Pay, Google Pay, or Credit/Debit card. No login required.
                </div>
              </div>
            </div>
            <a 
              href="#/pay/{unit.pay_token || 'demo-token'}" 
              target="_blank"
              class="btn btn-sm btn-primary px-5 rounded-xl font-bold shadow-md hover:shadow-lg transition-all gap-2 text-white shrink-0 w-full sm:w-auto"
            >
              <span>Pay {formatCurrency(unit.total_billed_cost_usd)} Online</span>
              <ArrowRight class="w-4 h-4" />
            </a>
          </div>
        {/if}

        <!-- Payment Remittance Notice -->
        <div class="border-t border-dashed border-slate-300 pt-4 text-[11px] text-slate-500 space-y-1">
          <p class="font-bold text-slate-700">Payment Notice:</p>
          <p>
            Please submit your utility payment in accordance with your residential lease agreement through the Resident Online Portal or via check payable to property management. Payments received after the due date may be subject to standard utility late fees.
          </p>
          <div class="flex justify-between items-center pt-2 font-mono text-[10px] text-slate-400">
            <span>Customer Service: support@saya.life • (800) 555-SAYA</span>
            <span>Remit Code: {String(unit.unit_id).slice(0, 8)}</span>
          </div>
        </div>

      </div>

    </div>
  </div>
{/if}

<style>
  @media print {
    :global(body) {
      background: white !important;
      color: black !important;
    }
  }
</style>
